import React, { useState, useMemo, useEffect } from "react";
import ChartistGraph from "react-chartist";
import {
  VictoryPie,
  VictoryTooltip,
  VictoryLabel,
  VictoryBar,
  VictoryLine,
  VictoryChart,
  VictoryAxis,
  VictoryScatter,
  VictoryVoronoiContainer,
  VictoryLegend,
  VictoryGroup,
  VictoryZoomContainer,
  VictoryBrushContainer,
} from "victory";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  Brush,
  BarChart,
  Area,
  AreaChart,
  Pie,
  PieChart,
  ReferenceLine,
  ReferenceArea,
  Cell,
} from "recharts";
import { VectorMap } from "react-jvectormap";
import {
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  Navbar,
  Nav,
  OverlayTrigger,
  Table,
  Tooltip,
  Container,
  Row,
  Col,
  Alert,
  Image,
} from "react-bootstrap";
import moment from "moment";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ColorRing, Dna } from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import CurrencyInput from "react-currency-input-field";
import ReactDatetime from "react-datetime";
import DataTable from "react-data-table-component";
import { useHistory } from "react-router-dom";
import { totalPenyaluran } from "stores";
import { totalNominalLunas } from "stores";
import { totalNominalOngoing } from "stores";
import { totalAllPengajuan } from "stores";
import { getTotalKaryawan } from "stores";
import { totalSimpanan } from "stores";
import { getProfitPugar } from "stores";
import { getPengajuanTrend, getUserTrend } from "stores";
import { getNominalProfitPugar } from "stores";
import { getPengajuanTrack } from "stores";
import dayjs from "dayjs";
import { getRekapSimpanan } from "stores";
import { getRekapAdministrasi } from "stores";
import { getRekapReport } from "stores";
import { syncDashboardHistory } from "stores";
import Swal from "sweetalert2";
import "./dashboard-sync.css";

function dashboardNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function dashboardMetricLoaded(value) {
  return value && !Array.isArray(value);
}

function ModernMetric({ icon, tone, label, value, detail }) {
  return (
    <article className={`modern-dashboard-metric ${tone}`}>
      <div className="modern-dashboard-metric-icon">
        <i className={icon} />
      </div>
      <div className="modern-dashboard-metric-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

function ModernEmptyChart({ text = "Belum ada data untuk ditampilkan" }) {
  return (
    <div className="modern-dashboard-empty-chart">
      <i className="fas fa-chart-line" />
      <span>{text}</span>
    </div>
  );
}

function ModernDashboard({
  reducerDashboard,
  formatRupiah,
  syncing,
  syncProgress,
  handleSyncDashboard,
  bulanTahun,
  setBulanTahun,
  dariTgl,
  smpTgl,
  setDariTgl,
  setSmpTgl,
}) {
  const totalKaryawan = reducerDashboard.totalKaryawan || [];
  const totalLunas = reducerDashboard.ambilTotalNominalLunas || [];
  const totalOngoing = reducerDashboard.ambilTotalNominalOnGoing || [];
  const totalPenyaluran = reducerDashboard.ambilTotalPenyaluran || [];
  const totalSimpanan = reducerDashboard.ambilTotalSimpanan || [];
  const totalPengajuan = reducerDashboard.ambilTotalAllPengajuan || [];

  const userTrend = (reducerDashboard.userTrack || [])
    .filter((item) => item && (item.bulan || item.date))
    .slice(-12)
    .map((item) => ({
      label: item.bulan || item.date,
      active: dashboardNumber(item.totalActive),
      join: dashboardNumber(item.totalJoin || item.joinCount || item.join),
      exit: dashboardNumber(item.totalExit || item.exitCount || item.exit),
    }));

  const savingTrend = (reducerDashboard.rekapSimpanan || [])
    .filter((item) => item && (item.date || item.bulan))
    .slice(-12)
    .map((item) => ({
      label: item.date || item.bulan,
      total: dashboardNumber(
        item.totalNominalBulan || item.totalNominalSimpanan,
      ),
    }));

  const applicationTrend = (reducerDashboard.pengajuanTrack || [])
    .filter((item) => item && item.bulan)
    .slice(-12)
    .map((item) => ({
      label: item.bulan,
      apply: dashboardNumber(item.nominalPengajuan),
      disburse: dashboardNumber(item.nominalPencairan),
    }));

  const adminTrend = (reducerDashboard.rekapAdmin || [])
    .filter((item) => item && item.date)
    .slice(-12)
    .map((item) => ({
      label: item.date,
      admin: dashboardNumber(item.totalAdmin),
      principal: dashboardNumber(item.totalPokok),
    }));

  const statusData = [
    {
      name: "Menunggu",
      value: dashboardNumber(totalPengajuan.totalMenunggu),
      color: "#f59e0b",
    },
    {
      name: "Diproses",
      value: dashboardNumber(totalPengajuan.totalDiproses),
      color: "#2563eb",
    },
    {
      name: "Disetujui",
      value: dashboardNumber(totalPengajuan.totalDisetujui),
      color: "#009b8f",
    },
    {
      name: "Revisi",
      value: dashboardNumber(totalPengajuan.totalRevisi),
      color: "#8b5cf6",
    },
    {
      name: "Ditolak",
      value: dashboardNumber(totalPengajuan.totalDitolak),
      color: "#ef4444",
    },
  ].filter((item) => item.value > 0);

  const statusTotal = statusData.reduce((sum, item) => sum + item.value, 0);
  const metricValue = (loaded, value, formatter = null) => {
    if (!dashboardMetricLoaded(loaded)) return "-";
    return formatter ? formatter(value) : value;
  };

  return (
    <section className="modern-dashboard">
      <div className="modern-dashboard-hero">
        <div className="modern-dashboard-brand">
          <img src={require("assets/img/pugar.png")} alt="PUGAR" />
        </div>
        <div className="modern-dashboard-hero-copy">
          <span className="modern-dashboard-eyebrow">
            Dashboard operasional
          </span>
          <h1>Dashboard PUGAR</h1>
          <p>
            Ringkasan data anggota, pengajuan, simpanan, pinjaman, dan
            administrasi dalam satu tampilan.
          </p>
          <div className="modern-dashboard-live-status">
            <span /> Data dashboard terhubung
          </div>
        </div>
        <div className="modern-dashboard-hero-action">
          <span className="modern-dashboard-action-label">
            {syncProgress
              ? `${syncProgress.current}/${syncProgress.total} - ${syncProgress.label}`
              : "Perbarui ringkasan dan grafik"}
          </span>
          <Button
            variant="primary"
            onClick={handleSyncDashboard}
            disabled={syncing}
          >
            <i className={`fas fa-sync-alt${syncing ? " fa-spin" : ""}`} />
            {syncing ? "Menyinkronkan..." : "Sinkronkan data"}
          </Button>
        </div>
      </div>

      {/* <div className="modern-dashboard-filters">
        <div className="modern-dashboard-filter-title">
          <i className="fas fa-sliders-h" />
          <div>
            <strong>Filter dashboard</strong>
            <span>Atur periode data yang ingin ditampilkan</span>
          </div>
        </div>
        <div className="modern-dashboard-filter-fields">
          <label>
            <span>Bulan dan tahun</span>
            <ReactDatetime
              inputProps={{ className: "form-control", readOnly: true, placeholder: "Pilih bulan" }}
              onChange={(event) => setBulanTahun(event.toDate())}
              value={bulanTahun}
              timeFormat={false}
              dateFormat="MMMM YYYY"
            />
          </label>
          <label>
            <span>Tanggal awal</span>
            <ReactDatetime
              inputProps={{ className: "form-control", readOnly: true, placeholder: "Pilih tanggal" }}
              onChange={(event) => {
                const date = event.toDate();
                date.setHours(0, 0, 0, 0);
                setDariTgl(date);
              }}
              value={dariTgl}
              timeFormat={false}
            />
          </label>
          <label>
            <span>Tanggal akhir</span>
            <ReactDatetime
              inputProps={{ className: "form-control", readOnly: true, placeholder: "Pilih tanggal" }}
              onChange={(event) => {
                const date = event.toDate();
                date.setHours(23, 59, 0, 0);
                setSmpTgl(date);
              }}
              value={smpTgl}
              timeFormat={false}
            />
          </label>
        </div>
      </div> */}

      {/* <div className="modern-dashboard-metrics">
        <ModernMetric
          icon="fas fa-users"
          tone="blue"
          label="Total karyawan"
          value={metricValue(totalKaryawan, totalKaryawan.totalUser)}
          detail="Informasi seluruh karyawan"
        />
        <ModernMetric
          icon="fas fa-user-check"
          tone="teal"
          label="Anggota koperasi"
          value={metricValue(totalKaryawan, totalKaryawan.anggotaKoperasi)}
          detail="Total anggota koperasi"
        />
        <ModernMetric
          icon="fas fa-file-signature"
          tone="amber"
          label="Total pengajuan"
          value={metricValue(totalPengajuan, totalPengajuan.totalPengajuan)}
          detail="Semua status pengajuan"
        />
        <ModernMetric
          icon="fas fa-wallet"
          tone="green"
          label="Total simpanan"
          value={metricValue(
            totalSimpanan,
            totalSimpanan.totalNominalSimpanan,
            formatRupiah,
          )}
          detail="Total nominal simpanan"
        />
        <ModernMetric
          icon="fas fa-money-bill-wave"
          tone="violet"
          label="Total penyaluran"
          value={metricValue(
            totalPenyaluran,
            totalPenyaluran.totalPenyaluran,
            formatRupiah,
          )}
          detail="Total nominal penyaluran"
        />
        <ModernMetric
          icon="fas fa-chart-line"
          tone="rose"
          label="Pinjaman berjalan"
          value={metricValue(
            totalOngoing,
            dashboardNumber(totalOngoing.totalSisaBayarPokok) +
              dashboardNumber(totalOngoing.totalSisaBayarAdmin),
            formatRupiah,
          )}
          detail="Total pinjaman yang masih berjalan"
        />
      </div>

      <div className="modern-dashboard-chart-grid">
        <section className="modern-dashboard-panel modern-dashboard-panel-wide">
          <div className="modern-dashboard-panel-head">
            <div>
              <span className="modern-dashboard-panel-kicker">Pergerakan anggota</span>
              <h2>Tren anggota</h2>
            </div>
            <span className="modern-dashboard-panel-period">12 periode terakhir</span>
          </div>
          <div className="modern-dashboard-chart">
            {userTrend.length === 0 ? (
              <ModernEmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={userTrend} margin={{ top: 12, right: 10, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="modernActiveFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e7edf4" vertical={false} strokeDasharray="4 4" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#738197" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#738197" }} axisLine={false} tickLine={false} />
                  <RechartsTooltip
                    formatter={(value, name) => [value, name === "active" ? "Aktif" : name === "join" ? "Join" : "Exit"]}
                    contentStyle={{ border: "1px solid #dbe3ed", borderRadius: 8, boxShadow: "0 8px 20px rgba(15,23,42,.1)" }}
                  />
                  <Area type="monotone" dataKey="active" stroke="#2563eb" strokeWidth={3} fill="url(#modernActiveFill)" />
                  <Bar dataKey="join" name="Join" fill="#009b8f" radius={[5, 5, 0, 0]} barSize={14} />
                  <Bar dataKey="exit" name="Exit" fill="#ef4444" radius={[5, 5, 0, 0]} barSize={14} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="modern-dashboard-legend">
            <span><i className="modern-legend-line blue" /> Aktif</span>
            <span><i className="modern-legend-dot teal" /> Join</span>
            <span><i className="modern-legend-dot red" /> Exit</span>
          </div>
        </section>

        <section className="modern-dashboard-panel modern-dashboard-panel-wide">
          <div className="modern-dashboard-panel-head">
            <div>
              <span className="modern-dashboard-panel-kicker">Aktivitas pinjaman</span>
              <h2>Tren pengajuan dan pencairan</h2>
            </div>
            <span className="modern-dashboard-panel-period">Nominal rupiah</span>
          </div>
          <div className="modern-dashboard-chart compact">
            {applicationTrend.length === 0 ? (
              <ModernEmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={applicationTrend} margin={{ top: 12, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#e7edf4" vertical={false} strokeDasharray="4 4" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#738197" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(value) => `${Math.round(value / 1000000)}jt`} tick={{ fontSize: 10, fill: "#738197" }} axisLine={false} tickLine={false} />
                  <RechartsTooltip formatter={(value, name) => [formatRupiah(value), name === "apply" ? "Pengajuan" : "Pencairan"]} contentStyle={{ border: "1px solid #dbe3ed", borderRadius: 8 }} />
                  <Bar dataKey="apply" name="Pengajuan" fill="#7c3aed" radius={[5, 5, 0, 0]} barSize={16} />
                  <Line type="monotone" dataKey="disburse" name="Pencairan" stroke="#16a34a" strokeWidth={3} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section className="modern-dashboard-panel">
          <div className="modern-dashboard-panel-head">
            <div>
              <span className="modern-dashboard-panel-kicker">Distribusi proses</span>
              <h2>Status pengajuan</h2>
            </div>
            <strong className="modern-dashboard-total-badge">{statusTotal}</strong>
          </div>
          <div className="modern-dashboard-donut-wrap">
            {statusData.length === 0 ? (
              <ModernEmptyChart text="Belum ada status pengajuan" />
            ) : (
              <>
                <div className="modern-dashboard-donut">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusData} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="88%" paddingAngle={3} stroke="none">
                        {statusData.map((item) => <Cell key={item.name} fill={item.color} />)}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="modern-dashboard-donut-label">
                    <strong>{statusTotal}</strong>
                    <span>Pengajuan</span>
                  </div>
                </div>
                <div className="modern-dashboard-status-list">
                  {statusData.map((item) => (
                    <div key={item.name}>
                      <span><i style={{ backgroundColor: item.color }} />{item.name}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="modern-dashboard-panel modern-dashboard-panel-wide">
          <div className="modern-dashboard-panel-head">
            <div>
              <span className="modern-dashboard-panel-kicker">Arus simpanan</span>
              <h2>Tren simpanan bulanan</h2>
            </div>
            <span className="modern-dashboard-panel-period">Nominal rupiah</span>
          </div>
          <div className="modern-dashboard-chart compact">
            {savingTrend.length === 0 ? (
              <ModernEmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={savingTrend} margin={{ top: 12, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="modernSavingFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#009b8f" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#009b8f" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e7edf4" vertical={false} strokeDasharray="4 4" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#738197" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(value) => `${Math.round(value / 1000000)}jt`} tick={{ fontSize: 10, fill: "#738197" }} axisLine={false} tickLine={false} />
                  <RechartsTooltip formatter={(value) => [formatRupiah(value), "Simpanan"]} contentStyle={{ border: "1px solid #dbe3ed", borderRadius: 8 }} />
                  <Area type="monotone" dataKey="total" stroke="#009b8f" strokeWidth={3} fill="url(#modernSavingFill)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section className="modern-dashboard-panel modern-dashboard-panel-wide">
          <div className="modern-dashboard-panel-head">
            <div>
              <span className="modern-dashboard-panel-kicker">Administrasi</span>
              <h2>Tren pokok dan administrasi</h2>
            </div>
            <span className="modern-dashboard-panel-period">Nominal rupiah</span>
          </div>
          <div className="modern-dashboard-chart compact">
            {adminTrend.length === 0 ? (
              <ModernEmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={adminTrend} margin={{ top: 12, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#e7edf4" vertical={false} strokeDasharray="4 4" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#738197" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(value) => `${Math.round(value / 1000000)}jt`} tick={{ fontSize: 10, fill: "#738197" }} axisLine={false} tickLine={false} />
                  <RechartsTooltip formatter={(value, name) => [formatRupiah(value), name === "admin" ? "Administrasi" : "Pokok"]} contentStyle={{ border: "1px solid #dbe3ed", borderRadius: 8 }} />
                  <Bar dataKey="principal" name="Pokok" fill="#2563eb" radius={[5, 5, 0, 0]} barSize={16} />
                  <Bar dataKey="admin" name="Administrasi" fill="#f59e0b" radius={[5, 5, 0, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section className="modern-dashboard-panel modern-dashboard-health">
          <div className="modern-dashboard-panel-head">
            <div>
              <span className="modern-dashboard-panel-kicker">Kesehatan portofolio</span>
              <h2>Ringkasan pinjaman</h2>
            </div>
            <i className="fas fa-shield-alt modern-dashboard-health-icon" />
          </div>
          <div className="modern-dashboard-health-list">
            <div><span>Total lunas</span><strong>{metricValue(totalLunas, totalLunas.jumlahPengajuanLunas)}</strong></div>
            <div><span>Total angsur</span><strong>{metricValue(totalOngoing, totalOngoing.jumlahPengajuanAngsur)}</strong></div>
            <div><span>Pokok berjalan</span><strong>{metricValue(totalOngoing, totalOngoing.totalSisaBayarPokok, formatRupiah)}</strong></div>
            <div><span>Admin berjalan</span><strong>{metricValue(totalOngoing, totalOngoing.totalSisaBayarAdmin, formatRupiah)}</strong></div>
          </div>
          <div className="modern-dashboard-health-note">
            <i className="fas fa-info-circle" />
            <span>Gunakan tombol sinkronisasi untuk memperbarui data history dan grafik.</span>
          </div>
        </section>
      </div> */}
    </section>
  );
}

function DashboardUtama() {
  const dispatch = useDispatch();
  const reducerDashboard = useSelector((state) => state.dashboardUtamaReducer);
  const [syncing, setSyncing] = React.useState(false);
  const [syncProgress, setSyncProgress] = React.useState(null);
  const formatBulan = (bulan) => {
    return dayjs(bulan).format("MMM 'YY");
  };
  function formatCurrency(amount) {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  }
  const [dariTgl, setDariTgl] = React.useState(() => {
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 1);
    twoYearsAgo.setMonth(0);
    twoYearsAgo.setDate(1);
    twoYearsAgo.setHours(0, 0, 0, 0);
    return twoYearsAgo.getTime();
  });
  const [smpTgl, setSmpTgl] = React.useState(new Date());
  const [bulanTahun, setBulanTahun] = React.useState(new Date());
  const [dailyActive, setDailyActive] = React.useState([]);
  const [monthlyJoin, setMonthlyJoin] = React.useState([]);
  const [monthlyExit, setMonthlyExit] = React.useState([]);

  const totalSisaBayarPokokLunas =
    reducerDashboard.ambilTotalNominalLunas.totalPokok;
  const totalSisaBayarAdminLunas =
    reducerDashboard.ambilTotalNominalLunas.totalAdmin;
  const totalJumlahLunas = totalSisaBayarPokokLunas + totalSisaBayarAdminLunas;

  const totalSisaBayarPokok =
    reducerDashboard.ambilTotalNominalOnGoing.totalSisaBayarPokok;
  const totalSisaBayarAdmin =
    reducerDashboard.ambilTotalNominalOnGoing.totalSisaBayarAdmin;
  const totalJumlahOnGoing = totalSisaBayarPokok + totalSisaBayarAdmin;

  const currentYear = new Date().getFullYear();
  const years = [];
  years.push({ value: "semua", label: "Semua" });
  for (let i = currentYear; i >= currentYear - 4; i--) {
    years.push({ value: String(i), label: String(i) });
  }
  const [selectedMonthYear, setSelectedMonthYear] = React.useState({
    start: moment().startOf("year").toDate(), // Jan (default)
    end: moment().endOf("year").toDate(), // Bulan sekarang
  });

  // const [selectedMonthYear, setSelectedMonthYear] = React.useState(new Date());
  // const [startDate, setStartDate] = React.useState(
  //   moment().startOf("year").startOf("month").toDate() // Januari tahun ini
  // );

  // const [endDate, setEndDate] = React.useState(
  //   moment().startOf("month").toDate() // Bulan sekarang
  // );

  const [isMinimized, setIsMinimized] = React.useState(false);

  const defaultOption = "semua";
  const defaultIndex = years.findIndex((year) => year.value === defaultOption);
  if (defaultIndex !== -1) {
    const defaultYear = years.splice(defaultIndex, 1)[0];
    years.unshift(defaultYear);
  }
  const formatRupiah = (angka) => {
    const numberFormat = new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    });
    return numberFormat.format(angka);
  };

  const sampleData = [
    { x: 1, y: 2 },
    { x: 2, y: 4 },
    { x: 3, y: 3 }, // label x = 3 akan berwarna hitam
    { x: 4, y: 5 },
    { x: 5, y: 4 },
  ];

  const dataNominal = reducerDashboard.profitNominal.map((item) => ({
    x: item.bulan,
    y: item.totalProfit,
  }));

  const dataKuantiti = reducerDashboard.profitKuantiti.map((item) => ({
    x: item.bulan,
    y: item.totalPengajuan,
  }));

  const dataTrendNominal = reducerDashboard.trendNominal.map((item) => ({
    x: item.bulan,
    y: item.totalNominal,
  }));

  const dataTrendKuantiti = reducerDashboard.trendKuantiti.map((item) => ({
    x: item.bulan,
    y: item.totalPengajuan,
  }));

  React.useEffect(() => {
    totalPenyaluran(dispatch, {
      tglAwal: moment(dariTgl).format("YYYY-MM-DD"),
      tglAkhir: moment(smpTgl).format("YYYY-MM-DD"),
    });
  }, [dariTgl, smpTgl]);

  React.useEffect(() => {
    totalNominalLunas(dispatch, {
      tglAwal: moment(dariTgl).format("YYYY-MM-DD"),
      tglAkhir: moment(smpTgl).format("YYYY-MM-DD"),
    });
  }, [dariTgl, smpTgl]);

  React.useEffect(() => {
    totalNominalOngoing(dispatch, {
      tglAwal: moment(dariTgl).format("YYYY-MM-DD"),
      tglAkhir: moment(smpTgl).format("YYYY-MM-DD"),
    });
  }, [dariTgl, smpTgl]);

  React.useEffect(() => {
    totalAllPengajuan(dispatch, {
      tglAwal: moment(dariTgl).format("YYYY-MM-DD"),
      tglAkhir: moment(smpTgl).format("YYYY-MM-DD"),
    });
  }, [dariTgl, smpTgl]);

  // React.useEffect(() => {
  //   if (!isLoading) {
  //     totalSimpanan(dispatch, {
  //       tahun: selectedYear,
  //     });
  //   }
  // }, [selectedYear, isLoading]);

  React.useEffect(() => {
    totalSimpanan(dispatch, {
      tglAwal: moment(dariTgl).format("YYYY-MM-DD"),
      tglAkhir: moment(smpTgl).format("YYYY-MM-DD"),
    });
  }, [dariTgl, smpTgl]);

  React.useEffect(() => {
    getTotalKaryawan(dispatch);
    getUserTrend(dispatch);
    getPengajuanTrack(dispatch);
    getRekapSimpanan(dispatch);
    getRekapAdministrasi(dispatch);
  }, []);

  const handleSyncDashboard = async () => {
    const confirmation = await Swal.fire({
      title: "Sinkronkan data dashboard?",
      text: "Lima proses sinkronisasi akan dijalankan berurutan. Proses dapat membutuhkan beberapa menit.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, sinkronkan",
      cancelButtonText: "Batal",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) return;

    setSyncing(true);
    setSyncProgress({ current: 1, total: 5, label: "Menyiapkan proses" });
    try {
      await syncDashboardHistory(setSyncProgress);
      setSyncProgress({ current: 5, total: 5, label: "Memuat data terbaru" });

      await Promise.all([
        getUserTrend(dispatch).catch(() => null),
        getPengajuanTrack(dispatch).catch(() => null),
        getRekapSimpanan(dispatch).catch(() => null),
        getRekapAdministrasi(dispatch).catch(() => null),
        getRekapReport(dispatch, {
          tgl: moment(bulanTahun).format("YYYY-MM"),
        }).catch(() => null),
      ]);

      await Swal.fire({
        icon: "success",
        title: "Sinkronisasi selesai",
        text: "Seluruh data dashboard berhasil diperbarui.",
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Sinkronisasi gagal",
        text: error.message || "Terjadi kesalahan saat sinkronisasi data.",
      });
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  React.useEffect(() => {
    const { start, end } = selectedMonthYear;

    if (!start || !end) return;

    const tahun = moment(start).year();
    const startBulan = moment(start).month() + 1;
    const endBulan = moment(end).month() + 1;

    getProfitPugar(dispatch, {
      tahun,
      startBulan,
      endBulan,
      type: "nominal",
    });

    getProfitPugar(dispatch, {
      tahun,
      startBulan,
      endBulan,
      type: "kuantiti",
    });

    getPengajuanTrend(dispatch, {
      tahun,
      startBulan,
      endBulan,
      type: "nominal",
    });

    getPengajuanTrend(dispatch, {
      tahun,
      startBulan,
      endBulan,
      type: "kuantiti",
    });
    getNominalProfitPugar(dispatch, {
      tahun,
      startBulan,
      endBulan,
      type: "nominal",
    });
  }, [selectedMonthYear, dispatch]);

  // React.useEffect(() => {
  // if (reducerDashboard.userTrack.length > 0) {
  //   transformCombined(reducerDashboard.userTrack);
  // }
  // }, [reducerDashboard]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    const renderUserList = (users = []) => {
      if (users.length === 0) return null;

      const preview = users.slice(0, 5);
      const remaining = users.length - 5;

      return (
        <>
          {preview.map((u, i) => (
            <div key={i} style={{ fontSize: 12 }}>
              • {u.nama}
            </div>
          ))}
          {remaining > 0 && (
            <div style={{ fontSize: 12, fontStyle: "italic" }}>
              +{remaining} lainnya...
            </div>
          )}
        </>
      );
    };

    return (
      <div
        style={{
          background: "#fff",
          padding: "14px",
          borderRadius: "10px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          maxWidth: 280,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {new Date(label + "-01").toLocaleDateString("id-ID", {
            month: "long",
            year: "numeric",
          })}
          {/* {new Date(label).toLocaleDateString("id-ID", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })} */}
        </div>

        <div style={{ color: "#2563eb", marginBottom: 6 }}>
          📊 Total Active: {data.totalActive}
        </div>

        {data.join > 0 && (
          <div style={{ marginBottom: 6 }}>
            <div style={{ color: "#16a34a", fontWeight: 500 }}>
              🟢 Join: {data.join}
            </div>
            {renderUserList(data.joinUsers)}
          </div>
        )}

        {Math.abs(data.exit) > 0 && (
          <div>
            <div style={{ color: "#dc2626", fontWeight: 500 }}>
              🔴 Exit: {Math.abs(data.exit)}
            </div>
            {renderUserList(data.exitUsers)}
          </div>
        )}
      </div>
    );
  };

  const DashboardAnggotaChart = ({ data }) => {
    const formattedData = useMemo(() => {
      return data
        .filter((d) => d && d.bulan)
        .map((d) => ({
          date: d.bulan,
          totalActive: d.totalActive,
          join: d.totalJoin,
          exit: -Math.abs(d.totalExit),
          joinUsers: d.joinUsers || [],
          exitUsers: d.exitUsers || [],
        }));
    }, [data]);
    const [range, setRange] = useState({
      startIndex: 0,
      endIndex: formattedData.length - 1,
    });
    const [activeIndex, setActiveIndex] = useState(null);

    const yearDividers = useMemo(() => {
      if (!formattedData.length) return [];

      const dividers = [];

      for (let i = 1; i < formattedData.length; i++) {
        const prevYear = formattedData[i - 1].date.split("-")[0];
        const currentYear = formattedData[i].date.split("-")[0];

        if (prevYear !== currentYear) {
          dividers.push({
            year: currentYear,
            date: formattedData[i].date, // bulan pertama tahun baru
          });
        }
      }

      return dividers;
    }, [formattedData]);
    return (
      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart
          data={formattedData}
          stackOffset="sign"
          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />

          {yearDividers.map((item) => (
            <ReferenceLine
              key={item.year}
              x={item.date}
              stroke="#94a3b8"
              strokeDasharray="4 4"
              strokeWidth={3}
              ifOverflow="extendDomain"
              yAxisId="left"
              // label={{
              //   value: item.year,
              //   position: "top",
              //   fill: "#64748b",
              //   fontSize: 12,
              //   fontWeight: 600,
              // }}
            />
          ))}

          <XAxis
            dataKey="date"
            interval={0}
            height={40}
            tick={({ x, y, payload }) => {
              const date = new Date(payload.value + "-01");
              const month = date.toLocaleDateString("id-ID", {
                month: "short",
              });
              const year = date.getFullYear().toString().slice(-2);

              return (
                <g transform={`translate(${x},${y})`}>
                  <text dy={10} textAnchor="middle" fontSize={12}>
                    {month}
                  </text>
                  <text
                    dy={20}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#64748b"
                  >
                    '{year}
                  </text>
                </g>
              );
            }}
            angle={formattedData.length > 18 ? -60 : 0}
          />

          {/* <YAxis yAxisId="left" width={60} />
          <YAxis yAxisId="right" width={60} orientation="right" />

          <RechartsTooltip content={<CustomTooltip />} />
          <Legend verticalAlign="top" />
          <ReferenceLine y={0} stroke="#000" /> */}
          <YAxis yAxisId="left" width={60} />
          <YAxis yAxisId="right" width={60} orientation="right" />

          <RechartsTooltip content={<CustomTooltip />} />
          <Legend verticalAlign="top" />
          <ReferenceLine y={0} yAxisId="left" stroke="#000" />

          {/* Line */}
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="totalActive"
            stroke="#2563eb"
            strokeWidth={3}
            dot={{ r: 4 }}
            name="Total Active"
            isAnimationActive={true}
          />
          <defs>
            <linearGradient id="joinGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22c55e" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#15803d" stopOpacity={0.9} />
            </linearGradient>

            <linearGradient id="exitGradient" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#f87171" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#b91c1c" stopOpacity={0.9} />
            </linearGradient>
            <filter id="shadow" height="200%">
              <feDropShadow
                dx="0"
                dy="2"
                stdDeviation="4"
                floodOpacity="0.15"
              />
            </filter>
          </defs>
          {/* Join */}
          <Bar
            yAxisId="right"
            dataKey="join"
            fill="url(#joinGradient)"
            stackId="stack"
            barSize={38}
            radius={[8, 8, 0, 0]}
            name="Join"
            isAnimationActive
            style={{ filter: "url(#shadow)" }}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            {formattedData.map((entry, index) => (
              <Cell
                key={`join-${index}`}
                fill="url(#joinGradient)"
                fillOpacity={
                  activeIndex === null ? 0.9 : activeIndex === index ? 1 : 0.3
                }
              />
            ))}
          </Bar>

          {/* Exit */}
          <Bar
            yAxisId="right"
            dataKey="exit"
            fill="url(#exitGradient)"
            stackId="stack"
            barSize={38}
            radius={[8, 8, 0, 0]}
            name="Exit"
            isAnimationActive
            style={{ filter: "url(#shadow)" }}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            {formattedData.map((entry, index) => (
              <Cell
                key={`exit-${index}`}
                fill="url(#exitGradient)"
                fillOpacity={
                  activeIndex === null ? 0.9 : activeIndex === index ? 1 : 0.3
                }
              />
            ))}
          </Bar>

          {/* 🔥 ZOOM CONTROL */}
          <Brush
            dataKey="date"
            height={30}
            stroke="#2563eb"
            startIndex={range.startIndex}
            endIndex={range.endIndex}
            onChange={(newRange) => {
              if (newRange?.startIndex !== undefined) {
                setRange(newRange);
              }
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  const CustomTooltipPengajuan = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <div className="font-bold">
            <b>{formatBulan(label)}</b>
          </div>
          {payload.map((item, index) => (
            <div key={index} style={{ color: item.color }}>
              {item.name}:{" "}
              {item.dataKey.includes("nominal")
                ? `Rp ${item.value.toLocaleString()}`
                : item.value}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const DashboardPengajuanCart = ({ data }) => {
    const yearSeparators = useMemo(() => {
      if (!data.length) return [];
      const dividers = [];
      for (let i = 1; i < data.length; i++) {
        const prevYear = data[i - 1].bulan.split("-")[0];
        const currentYear = data[i].bulan.split("-")[0];

        if (prevYear !== currentYear) {
          dividers.push({ year: currentYear, date: data[i].bulan });
        }
      }

      return dividers;
    }, [data]);

    return (
      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          {yearSeparators.map((item) => (
            <ReferenceLine
              key={item.year}
              x={item.date}
              stroke="#94a3b8"
              strokeDasharray="4 4"
              strokeWidth={3}
              yAxisId="left"
            />
          ))}
          <XAxis
            dataKey="bulan"
            interval={0}
            height={40}
            tick={({ x, y, payload }) => {
              const date = new Date(payload.value + "-01");
              const month = date.toLocaleDateString("id-ID", {
                month: "short",
              });
              const year = date.getFullYear().toString().slice(-2);

              return (
                <g transform={`translate(${x},${y})`}>
                  <text dy={10} textAnchor="middle" fontSize={12}>
                    {month}
                  </text>
                  <text
                    dy={20}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#64748b"
                  >
                    '{year}
                  </text>
                </g>
              );
            }}
            // angle={formattedData.length > 18 ? -60 : 0}
          />

          {/* Y Axis Nominal */}
          <YAxis
            yAxisId="left"
            tickFormatter={(val) => `${val / 1000000}jt`}
            width={60}
          />

          {/* Y Axis Status */}
          <YAxis yAxisId="right" orientation="right" width={60} />

          <RechartsTooltip content={<CustomTooltipPengajuan />} />
          <Legend />

          {/* BAR NOMINAL PENGAJUAN */}
          <Bar
            yAxisId="left"
            dataKey="nominalPengajuan"
            name="Nominal Pengajuan"
            radius={[10, 10, 0, 0]}
            barSize={30}
            fill="#6366F1"
          />

          {/* BAR NOMINAL PENCAIRAN */}
          <Bar
            yAxisId="left"
            dataKey="nominalPencairan"
            name="Nominal Pencairan"
            radius={[10, 10, 0, 0]}
            barSize={30}
            fill="#22C55E"
          />

          {/* LINE STATUS */}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="Menunggu"
            stroke="#F59E0B"
            strokeWidth={3}
            dot={{ r: 4 }}
          />

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="Diproses"
            stroke="#EF4444"
            strokeWidth={3}
            dot={{ r: 4 }}
          />

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="Disetujui"
            stroke="#0EA5E9"
            strokeWidth={3}
            dot={{ r: 4 }}
          />

          <Brush
            dataKey="bulan"
            height={30}
            stroke="#6366F1"
            travellerWidth={10}
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  const CustomTooltipRekapSimpanan = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;

      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 8,
            padding: "10px 14px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: 0, fontWeight: 600 }}>{label}</p>
          <p style={{ margin: 0, color: "#2563eb", fontWeight: 600 }}>
            Rp {value.toLocaleString("id-ID")}
          </p>
        </div>
      );
    }
    return null;
  };

  const DashboardSimpananBarChart = ({ data }) => {
    const formattedData = data.map((item) => ({
      ...item,
      month: new Date(item.date + "-01").toLocaleDateString("id-ID", {
        month: "short",
        year: "numeric",
      }),
    }));
    const yearSeparators = useMemo(() => {
      if (!data.length) return [];
      const dividers = [];
      for (let i = 1; i < data.length; i++) {
        const prevYear = data[i - 1].date.split("-")[0];
        const currentYear = data[i].date.split("-")[0];

        if (prevYear !== currentYear) {
          dividers.push({ year: currentYear, date: data[i].date });
        }
      }

      return dividers;
    }, [data]);

    return (
      <div style={{ width: "100%", height: 400 }}>
        <ResponsiveContainer>
          <BarChart
            data={formattedData}
            margin={{ top: 20, right: 20, left: 10, bottom: 40 }}
          >
            {/* gradient */}
            <defs>
              <linearGradient id="barColor" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                <stop offset="100%" stopColor="#93c5fd" stopOpacity={0.7} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" />
            {yearSeparators.map((item) => (
              <ReferenceLine
                key={item.year}
                x={item.date}
                stroke="#94a3b8"
                strokeDasharray="4 4"
                strokeWidth={3}
                yAxisId="left"
              />
            ))}
            <XAxis
              dataKey="date"
              interval={0}
              height={40}
              tick={({ x, y, payload }) => {
                const date = new Date(payload.value + "-01");
                const month = date.toLocaleDateString("id-ID", {
                  month: "short",
                });
                const year = date.getFullYear().toString().slice(-2);

                return (
                  <g transform={`translate(${x},${y})`}>
                    <text dy={10} textAnchor="middle" fontSize={12}>
                      {month}
                    </text>
                    <text
                      dy={20}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#64748b"
                    >
                      '{year}
                    </text>
                  </g>
                );
              }}
              // angle={formattedData.length > 18 ? -60 : 0}
            />

            <YAxis
              yAxisId="left"
              tickFormatter={(v) =>
                (v / 1000000).toLocaleString("id-ID") + "jt"
              }
            />

            <RechartsTooltip content={<CustomTooltipRekapSimpanan />} />

            <Bar
              yAxisId="left"
              dataKey="totalNominalBulan"
              fill="url(#barColor)"
              radius={[8, 8, 0, 0]}
              barSize={35}
              animationDuration={1200}
              animationEasing="ease-out"
            >
              {formattedData.map((entry, index) => (
                <Cell key={index} />
              ))}
            </Bar>

            {/* zoom slider */}
            <Brush
              dataKey="date"
              height={25}
              stroke="#3b82f6"
              travellerWidth={12}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const CustomTooltipRekapAdmin = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;

      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 8,
            padding: "10px 14px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: 0, fontWeight: 600 }}>{label}</p>
          <p style={{ margin: 0, color: "#2563eb", fontWeight: 600 }}>
            Rp {value.toLocaleString("id-ID")}
          </p>
        </div>
      );
    }
    return null;
  };

  const DashboardAdminBarChart = ({ data }) => {
    const [zoomedTotalAdmin, setZoomedTotalAdmin] = useState(0);

    const [brushIndex, setBrushIndex] = useState({
      startIndex: 0,
      endIndex: data.length > 0 ? data.length - 1 : 0,
    });

    // Format data
    const formattedData = useMemo(() => {
      return data.map((item) => ({
        ...item,
        month: new Date(item.date + "-01").toLocaleDateString("id-ID", {
          month: "short",
          year: "numeric",
        }),
      }));
    }, [data]);

    // Inisialisasi total awal
    useEffect(() => {
      if (formattedData.length > 0) {
        const total = formattedData.reduce(
          (sum, item) => sum + (item.totalAdmin || 0),
          0,
        );

        setZoomedTotalAdmin(total);

        setBrushIndex({
          startIndex: 0,
          endIndex: formattedData.length - 1,
        });
      }
    }, [formattedData]);

    // Brush change
    const handleBrushChange = (brushState) => {
      if (!brushState || brushState.startIndex === undefined) return;

      const { startIndex, endIndex } = brushState;

      setBrushIndex({ startIndex, endIndex });

      const slicedData = formattedData.slice(startIndex, endIndex + 1);

      const total = slicedData.reduce(
        (sum, item) => sum + (item.totalAdmin || 0),
        0,
      );

      setZoomedTotalAdmin(total);
    };

    // Separator tahun
    const yearSeparators = useMemo(() => {
      if (!data.length) return [];

      const dividers = [];

      for (let i = 1; i < data.length; i++) {
        const prevYear = data[i - 1].date.split("-")[0];
        const currentYear = data[i].date.split("-")[0];

        if (prevYear !== currentYear) {
          dividers.push({
            year: currentYear,
            date: data[i].date,
          });
        }
      }

      return dividers;
    }, [data]);

    return (
      <div style={{ width: "100%", height: 400 }}>
        <div
          style={{
            fontWeight: 600,
            paddingLeft: 20,
            paddingRight: 20,
            justifyContent: "center",
            display: "flex",
            fontSize: 20,
          }}
        >
          {formatCurrency(zoomedTotalAdmin)}
        </div>

        <ResponsiveContainer>
          <BarChart
            data={formattedData}
            margin={{ top: 20, right: 20, left: 10, bottom: 40 }}
          >
            <defs>
              <linearGradient id="barColor" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                <stop offset="100%" stopColor="#93c5fd" stopOpacity={0.7} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" />

            {yearSeparators.map((item) => (
              <ReferenceLine
                key={item.year}
                x={item.date}
                stroke="#94a3b8"
                strokeDasharray="4 4"
                strokeWidth={3}
                yAxisId="left"
              />
            ))}

            <XAxis
              dataKey="date"
              interval={0}
              height={40}
              tick={({ x, y, payload }) => {
                const date = new Date(payload.value + "-01");

                const month = date.toLocaleDateString("id-ID", {
                  month: "short",
                });

                const year = date.getFullYear().toString().slice(-2);

                return (
                  <g transform={`translate(${x},${y})`}>
                    <text dy={10} textAnchor="middle" fontSize={12}>
                      {month}
                    </text>
                    <text
                      dy={20}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#64748b"
                    >
                      '{year}
                    </text>
                  </g>
                );
              }}
            />

            <YAxis
              yAxisId="left"
              tickFormatter={(v) =>
                (v / 1000000).toLocaleString("id-ID") + "jt"
              }
            />

            <RechartsTooltip content={<CustomTooltipRekapAdmin />} />

            <Bar
              yAxisId="left"
              dataKey="totalAdmin"
              fill="url(#barColor)"
              radius={[8, 8, 0, 0]}
              barSize={35}
            />

            <Brush
              dataKey="date"
              height={25}
              stroke="#3b82f6"
              travellerWidth={12}
              startIndex={brushIndex.startIndex}
              endIndex={brushIndex.endIndex}
              onChange={handleBrushChange}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  React.useEffect(() => {
    getRekapReport(dispatch, {
      tgl: moment(bulanTahun).format("YYYY-MM"),
    });
  }, [bulanTahun]);
  const TableIn = () => {
    const tableStyle = {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      background: "#fff",
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      fontSize: "14px",
    };

    const thStyle = {
      padding: "12px",
      textAlign: "center",
      background: "#f8fafc",
      color: "#334155",
      fontWeight: "600",
      borderBottom: "1px solid #e2e8f0",
    };

    const tdBase = {
      padding: "12px",
      borderBottom: "1px solid #f1f5f9",
    };

    const tdLeft = {
      ...tdBase,
      textAlign: "left",
      color: "#334155",
      fontWeight: "500",
    };

    const tdCenter = {
      ...tdBase,
      textAlign: "center",
      color: "#0f172a",
    };

    const tdRight = {
      ...tdBase,
      textAlign: "right",
      color: "#0f172a",
      fontWeight: "600",
    };

    const rowStyle = {
      transition: "all 0.2s ease",
      cursor: "default",
    };

    const hoverStyle = {
      backgroundColor: "#f9fafb",
    };

    let dataIn =
      reducerDashboard.rekapReport && reducerDashboard.rekapReport.responIn;

    return (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}></th>
            <th style={thStyle}>Jumlah</th>
            <th style={thStyle}>Nominal</th>
            <th style={thStyle}>Admin</th>
          </tr>
        </thead>
        <tbody>
          {dataIn &&
            dataIn.map((row, i) => (
              <tr
                key={i}
                style={rowStyle}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f9fafb")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <td style={tdLeft}>{row.label}</td>
                <td style={tdCenter}>{row.anggota}</td>
                <td style={tdRight}>{formatRupiah(row.nominal)}</td>
                <td style={tdRight}>{formatRupiah(row.admin)}</td>
              </tr>
            ))}
        </tbody>
      </table>
    );
  };

  const TableOut = () => {
    const tableStyle = {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      background: "#fff",
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      fontSize: "14px",
    };

    const thStyle = {
      padding: "12px",
      textAlign: "center",
      background: "#fff1f2", // beda dikit (merah soft)
      color: "#7f1d1d",
      fontWeight: "600",
      borderBottom: "1px solid #fecdd3",
    };

    const tdBase = {
      padding: "12px",
      borderBottom: "1px solid #f1f5f9",
    };

    const tdLeft = {
      ...tdBase,
      textAlign: "left",
      color: "#334155",
      fontWeight: "500",
    };

    const tdCenter = {
      ...tdBase,
      textAlign: "center",
      color: "#0f172a",
    };

    const tdRight = {
      ...tdBase,
      textAlign: "right",
      color: "#b91c1c", // merah untuk pengeluaran
      fontWeight: "600",
    };

    let dataOut =
      reducerDashboard.rekapReport && reducerDashboard.rekapReport.responOut;
    return (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}></th>
            <th style={thStyle}>Jumlah</th>
            <th style={thStyle}>Nominal</th>
            <th style={thStyle}>Admin</th>
          </tr>
        </thead>
        <tbody>
          {dataOut &&
            dataOut.map((row, i) => (
              <tr
                key={i}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#fef2f2")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
                style={{ transition: "all 0.2s ease" }}
              >
                <td style={tdLeft}>{row.label}</td>
                <td style={tdCenter}>{row.anggota}</td>
                <td style={tdRight}>{formatRupiah(row.nominal)}</td>
                <td style={tdRight}>{formatRupiah(row.admin)}</td>
              </tr>
            ))}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <ModernDashboard
        reducerDashboard={reducerDashboard}
        formatRupiah={formatRupiah}
        syncing={syncing}
        syncProgress={syncProgress}
        handleSyncDashboard={handleSyncDashboard}
        bulanTahun={bulanTahun}
        setBulanTahun={setBulanTahun}
        dariTgl={dariTgl}
        smpTgl={smpTgl}
        setDariTgl={setDariTgl}
        setSmpTgl={setSmpTgl}
      />
      <Card
        style={{
          // boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px",
          border: 0,
          borderRadius: 10,
          marginTop: 15,
        }}
      >
        <div style={{ maxHeight: "800px" }}>
          <Row>
            <Container className="px-2 text-center">
              <Col>
                <Row>
                  {/* <Col className="d-flex align-items-center justify-content-start">
                    <img
                      alt="..."
                      src={require("assets/img/pugar.png").default}
                      width="auto"
                      height="150px"
                    ></img>
                  </Col> */}
                  <Col className="d-flex flex-column align-items-center justify-content-center text-center">
                    <h4 className="title mt-2" style={{ fontWeight: "bold" }}>
                      Bulan Tahun
                    </h4>

                    <Form.Group
                      style={{
                        boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                        width: "100%", // opsional biar input tetap full
                        maxWidth: "300px", // biar tidak terlalu lebar
                      }}
                    >
                      <ReactDatetime
                        inputProps={{
                          className: "form-control text-center", // ini biar teks input di tengah
                          placeholder: "Pilih Bulan dan Tahun",
                          readOnly: true,
                          style: {
                            cursor: "default",
                            color: "black",
                            backgroundColor: "white",
                          },
                        }}
                        onChange={(e) => {
                          setBulanTahun(e.toDate());
                          console.log(e.toDate(), "tes ambil bulan tahun");
                        }}
                        value={bulanTahun}
                        initialValue={bulanTahun}
                        dateFormat="MMMM YYYY"
                        timeFormat={false}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              {/* <Col
                      lg={{ span: 6, offset: 6 }}
                      style={{
                        marginBottom: 20,
                        // marginLeft: 10,
                        marginRight: 10,
                      }}
                    >
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="singleSelect"
                        // value={status}
                        // onChange={(selectedOption) => setStatus(selectedOption)}
                        // onChange={setStatus}
                        options={[
                          { value: "true", label: "Aktif" },
                          { value: "false", label: "Tidak Aktif" },
                        ]}
                        placeholder="Pilih"
                      />
                    </Col> */}
              <Row>
                <Col md="6">
                  <div
                    style={{
                      marginBottom: 10,
                      textTransform: "uppercase",
                      color: "#534b4bff",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: 24,
                        textTransform: "none",
                      }}
                    >
                      In
                    </span>
                  </div>
                  <TableIn />
                </Col>
                <Col md="6">
                  <div
                    style={{
                      marginBottom: 10,
                      textTransform: "uppercase",
                      color: "#534b4bff",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: 24,
                        textTransform: "none",
                      }}
                    >
                      Out
                    </span>
                  </div>
                  <TableOut />
                </Col>
              </Row>
            </Container>
            <Container style={{ marginTop: 20 }}>
              <Col className=" justify-content-end">
                <Row>
                  <Col md="6">
                    <h4 className="title mt-2" style={{ fontWeight: "bold" }}>
                      Tanggal Awal
                    </h4>
                    <Form.Group
                      style={{
                        boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                      }}
                    >
                      <ReactDatetime
                        inputProps={{
                          className: "form-control",
                          placeholder: "Date Picker Here",
                          readOnly: true,
                          style: {
                            cursor: "default",
                            color: "black",
                            backgroundColor: "white",
                          },
                        }}
                        onChange={(e) => {
                          (e.toDate().setHours(0, 0, 0, 0),
                            setDariTgl(e.toDate().setHours(0, 0, 0, 0)));
                        }}
                        value={dariTgl}
                        initialValue={dariTgl}
                        timeFormat={false}
                      ></ReactDatetime>
                    </Form.Group>
                  </Col>
                  <Col md="6">
                    <h4 className="title mt-2" style={{ fontWeight: "bold" }}>
                      Tanggal Akhir
                    </h4>
                    <Form.Group
                      style={{
                        boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                      }}
                    >
                      <ReactDatetime
                        inputProps={{
                          className: "form-control",
                          placeholder: "Date Picker Here",
                          readOnly: true,
                          style: {
                            cursor: "default",
                            color: "black",
                            backgroundColor: "white",
                          },
                        }}
                        onChange={(e) => {
                          (e.toDate().setHours(23, 59, 0, 0),
                            setSmpTgl(e.toDate().setHours(23, 59, 0, 0)));
                        }}
                        value={smpTgl}
                        initialValue={smpTgl}
                        timeFormat={false}
                      ></ReactDatetime>
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Container>
          </Row>
        </div>
      </Card>
      <Container>
        <Row>
          <Col md="6">
            <Card
              style={{
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                borderRadius: 10,
                marginLeft: 10,
              }}
            >
              <Card.Header
                className="d-none d-md-inline"
                style={{
                  backgroundColor: "#7A5DBE",
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                }}
              >
                <Card.Title
                  as="h6"
                  style={{
                    textAlign: "center",
                    alignItems: "center",
                    marginBottom: 10,
                    fontWeight: "bold",
                  }}
                >
                  <b style={{ color: "white", fontSize: 20 }}>
                    Total Penyaluran
                  </b>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <div
                  style={{
                    fontSize: 20,
                    textAlign: "center",
                    marginTop: 10,
                    color: "#212121",
                  }}
                >
                  <div
                    style={{
                      fontSize: 20,
                      textAlign: "center",
                      marginTop: 10,
                      color: "#212121",
                    }}
                  >
                    {reducerDashboard.ambilTotalPenyaluran.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={200} />
                      </SkeletonTheme>
                    ) : (
                      formatRupiah(
                        reducerDashboard.ambilTotalPenyaluran.totalPenyaluran,
                      )
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md="6">
            <Card
              style={{
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                borderRadius: 10,
                marginRight: 10,
              }}
            >
              <Card.Header
                className="d-none d-md-inline"
                style={{
                  backgroundColor: "#7A5DBE",
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                }}
              >
                <Card.Title
                  as="h6"
                  style={{
                    textAlign: "center",
                    alignItems: "center",
                    marginBottom: 10,
                    fontWeight: "bold",
                  }}
                >
                  <b style={{ color: "white", fontSize: 20 }}>Total Simpanan</b>
                </Card.Title>
              </Card.Header>
              <Card.Body
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <>
                  <Col
                    md="12"
                    style={{
                      fontSize: 20,
                      color: "#212121",
                      // marginLeft: 20,
                      textAlign: "center",
                    }}
                  >
                    {reducerDashboard.ambilTotalSimpanan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={120} />
                      </SkeletonTheme>
                    ) : (
                      formatRupiah(
                        reducerDashboard.ambilTotalSimpanan
                          .totalNominalSimpanan,
                      )
                    )}
                  </Col>
                </>
              </Card.Body>
            </Card>
          </Col>

          <Col md="6">
            <Card
              style={{
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                borderRadius: 10,
                marginLeft: 10,
                // marginTop: 20,
              }}
            >
              <Card.Header
                className="d-none d-md-inline"
                style={{
                  backgroundColor: "#7A5DBE",
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                }}
              >
                <Card.Title
                  as="h6"
                  style={{
                    textAlign: "center",
                    alignItems: "center",
                    marginBottom: 10,
                    fontWeight: "bold",
                  }}
                >
                  <b style={{ color: "white" }}>Total Lunas</b>
                </Card.Title>
              </Card.Header>
              <Card.Body style={{ textAlign: "center", fontSize: 25 }}>
                {isNaN(totalJumlahLunas) ? (
                  <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                    <Skeleton height={"100%"} width={120} />
                  </SkeletonTheme>
                ) : (
                  formatRupiah(totalJumlahLunas)
                )}
              </Card.Body>
              <Row>
                <Col
                  className="p-2 d-flex justify-content-center"
                  style={{ fontWeight: "bold" }}
                >
                  Pokok
                </Col>

                <Col
                  className="p-2 d-flex justify-content-center"
                  style={{ fontWeight: "bold" }}
                >
                  Admin
                </Col>
              </Row>
              <Row>
                <Col
                  className="p-2 d-flex justify-content-center"
                  // style={{ fontWeight: "bold" }}
                >
                  {reducerDashboard.ambilTotalNominalLunas.length === 0 ? (
                    <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                      <Skeleton height={"100%"} width={90} />
                    </SkeletonTheme>
                  ) : (
                    formatRupiah(
                      reducerDashboard.ambilTotalNominalLunas.totalPokok,
                    )
                  )}
                </Col>

                <Col className="p-2 d-flex justify-content-center">
                  {reducerDashboard.ambilTotalNominalLunas.length === 0 ? (
                    <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                      <Skeleton height={"100%"} width={90} />
                    </SkeletonTheme>
                  ) : (
                    formatRupiah(
                      reducerDashboard.ambilTotalNominalLunas.totalAdmin,
                    )
                  )}
                </Col>
              </Row>
            </Card>
          </Col>

          <Col md="6">
            <Card
              style={{
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                borderRadius: 10,
                marginRight: 10,
                // marginTop: 20,
              }}
            >
              <Card.Header
                className="d-none d-md-inline"
                style={{
                  backgroundColor: "#7A5DBE",
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                }}
              >
                <Card.Title
                  as="h6"
                  style={{
                    textAlign: "center",
                    alignItems: "center",
                    marginBottom: 10,
                    fontWeight: "bold",
                  }}
                >
                  <b style={{ color: "white" }}>Total On Going</b>
                </Card.Title>
              </Card.Header>
              <Card.Body style={{ textAlign: "center", fontSize: 25 }}>
                {isNaN(totalJumlahOnGoing) ? (
                  <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                    <Skeleton height={"100%"} width={120} />
                  </SkeletonTheme>
                ) : (
                  formatRupiah(totalJumlahOnGoing)
                )}
              </Card.Body>
              <Row>
                <Col
                  className="p-2 d-flex justify-content-center"
                  style={{ fontWeight: "bold" }}
                >
                  Pokok
                </Col>

                <Col
                  className="p-2 d-flex justify-content-center"
                  style={{ fontWeight: "bold" }}
                >
                  Admin
                </Col>
              </Row>
              <Row>
                <Col
                  className="p-2 d-flex justify-content-center"
                  // style={{ fontWeight: "bold" }}
                >
                  {reducerDashboard.ambilTotalNominalOnGoing.length === 0 ? (
                    <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                      <Skeleton height={"100%"} width={90} />
                    </SkeletonTheme>
                  ) : (
                    formatRupiah(
                      reducerDashboard.ambilTotalNominalOnGoing
                        .totalSisaBayarPokok,
                    )
                  )}
                </Col>
                <Col
                  className="p-2 d-flex justify-content-center"
                  // style={{ fontWeight: "bold" }}
                >
                  {reducerDashboard.ambilTotalNominalOnGoing.length === 0 ? (
                    <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                      <Skeleton height={"100%"} width={90} />
                    </SkeletonTheme>
                  ) : (
                    formatRupiah(
                      reducerDashboard.ambilTotalNominalOnGoing
                        .totalSisaBayarAdmin,
                    )
                  )}
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>
      <Card
        style={{
          boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
          borderRadius: 10,
        }}
      >
        <Card.Body>
          <div
            style={{
              marginBottom: 10,
              textTransform: "uppercase",
              color: "#534b4bff",
            }}
          >
            <span
              style={{
                fontWeight: "600",
                fontSize: 24,
                textTransform: "none",
              }}
            >
              User Growth Trend
            </span>
          </div>
          <hr></hr>
          <Row>
            <Col>
              <Row>
                <DashboardAnggotaChart data={reducerDashboard.userTrack} />
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card
        style={{
          boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
          borderRadius: 10,
        }}
      >
        <Card.Body>
          <div
            style={{
              marginBottom: 10,
              textTransform: "uppercase",
              color: "#534b4bff",
            }}
          >
            <span
              style={{
                fontWeight: "600",
                fontSize: 24,
                textTransform: "none",
              }}
            >
              Pengajuan Growth Trend
            </span>
          </div>
          <hr></hr>
          <Row>
            <Col>
              <Row>
                <DashboardPengajuanCart
                  data={reducerDashboard.pengajuanTrack}
                />
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card
        style={{
          boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
          borderRadius: 10,
        }}
      >
        <Card.Body>
          <div
            style={{
              marginBottom: 10,
              textTransform: "uppercase",
              color: "#534b4bff",
            }}
          >
            <span
              style={{
                fontWeight: "600",
                fontSize: 24,
                textTransform: "none",
              }}
            >
              Trend Simpanan
            </span>
          </div>
          <hr></hr>
          <Row>
            <Col>
              <Row>
                <DashboardSimpananBarChart
                  data={reducerDashboard.rekapSimpanan}
                />
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card
        style={{
          boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
          borderRadius: 10,
        }}
      >
        <Card.Body>
          <div
            style={{
              marginBottom: 10,
              textTransform: "uppercase",
              color: "#534b4bff",
            }}
          >
            <span
              style={{
                fontWeight: "600",
                fontSize: 24,
                textTransform: "none",
              }}
            >
              Trend Administrasi
            </span>
          </div>
          <hr></hr>
          <Row>
            <Col>
              <Row>
                <DashboardAdminBarChart data={reducerDashboard.rekapAdmin} />
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Container fluid>
        <Row>
          <Col lg="6" sm="6">
            <h4
              className="title mt-2"
              style={{ fontWeight: "bold", textTransform: "uppercase" }}
            >
              informasi
              <hr></hr>
            </h4>
            <Row>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Total Karyawan</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.totalKaryawan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.totalKaryawan.totalUser
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Total Lunas</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.ambilTotalNominalLunas.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.ambilTotalNominalLunas
                        .jumlahPengajuanLunas
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Total Angsur</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.ambilTotalNominalOnGoing.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.ambilTotalNominalOnGoing
                        .jumlahPengajuanAngsur
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Total Anggota Koperasi</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.totalKaryawan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.totalKaryawan.anggotaKoperasi
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Bukan Anggota Koperasi</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.totalKaryawan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.totalKaryawan.bukanAnggotaKoperasi
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>User Menunggu Approve</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.totalKaryawan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.totalKaryawan.userMenungguRegister
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>User Lama Belum Register</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.totalKaryawan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.totalKaryawan.userLamaBelumRegister
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
          <Col lg="6" sm="6">
            <h4
              className="title mt-2"
              style={{ fontWeight: "bold", textTransform: "uppercase" }}
            >
              status pengajuan
              <hr></hr>
            </h4>
            <Row>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Total Pengajuan</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.ambilTotalAllPengajuan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.ambilTotalAllPengajuan.totalPengajuan
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Menunggu</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.ambilTotalAllPengajuan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.ambilTotalAllPengajuan.totalMenunggu
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Diproses</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.ambilTotalAllPengajuan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.ambilTotalAllPengajuan.totalDiproses
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Disetujui</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.ambilTotalAllPengajuan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.ambilTotalAllPengajuan.totalDisetujui
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Revisi</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.ambilTotalAllPengajuan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.ambilTotalAllPengajuan.totalRevisi
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg="4" sm="6">
                <Card
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                    borderRadius: 10,
                  }}
                >
                  <Card.Header
                    className="d-none d-md-inline"
                    style={{
                      backgroundColor: "#624B99",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  >
                    <Card.Title
                      as="h6"
                      style={{
                        textAlign: "center",
                        alignItems: "center",
                        marginBottom: 10,
                        fontWeight: "bold",
                      }}
                    >
                      <b style={{ color: "white" }}>Ditolak</b>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body style={{ textAlign: "center" }}>
                    {reducerDashboard.ambilTotalAllPengajuan.length === 0 ? (
                      <SkeletonTheme color="#BDC7C9" highlightColor="#BDC7C9">
                        <Skeleton height={"100%"} width={50} />
                      </SkeletonTheme>
                    ) : (
                      reducerDashboard.ambilTotalAllPengajuan.totalDitolak
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default DashboardUtama;
